import React, { useState, useEffect, useContext, useMemo } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { MonthCascaderField } from '@/components/molecules/month-cascader-field'
import { getReservationsBoundaryDates } from '@/apis/aipass'
import { Dayjs } from 'dayjs'
import { DashboardSalesFilterType, DashboardType } from '@/models/sales-manager/dashboard'
import { Link } from 'react-router-dom'
import { Popup } from '@/components/atoms/popup'
import { Checkbox } from '@/components/atoms/checkbox'
import { SalesManagerDashboardFilterOption } from '@/models/local-storage'
import { AccountContext } from '@/contexts/account'
import { ToggleButton } from '../../settings/toggle-button'

type Props = {
  openSalesManagerId: string | undefined
  month: Dayjs
  mode: DashboardType
  onChangeMonth: (month: Dayjs) => void
  onChangeMode: (mode: DashboardType) => void
  onChangeFilter: (filter: DashboardSalesFilterType) => void
}

export const ToolBar: React.FC<Props> = ({ openSalesManagerId, month, mode, onChangeMonth, onChangeMode, onChangeFilter }) => {
  const { t } = useTranslation()
  const { account } = useContext<any>(AccountContext)
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(month)
  const [reservationsBoundaryDates, setReservationsBoundaryDates] = useState<{ min: string; max: string }>()
  const [isShowFilterPopup, setIsShowFilterPopup] = useState<boolean>(false)
  const [salesFilterOptions, setSalesFilterOptions] = useState<DashboardSalesFilterType>(
    localStorage.getItem(SalesManagerDashboardFilterOption(account.hotel.id))
      ? {
          // 追加プロパティのためstorageにない場合のデフォルトを設定
          isTaxIncluded: true,
          ...JSON.parse(localStorage.getItem(SalesManagerDashboardFilterOption(account.hotel.id))!),
        }
      : {
          isTaxIncluded: true,
          stayType: {
            stay: true,
            dayUse: true,
          },
          salesType: {
            room: true,
            other: true,
            temporary: false,
          },
        },
  )

  const moveMonth = (value: number) => {
    setSelectedMonth(selectedMonth.clone().add(value, 'month'))
  }

  useEffect(() => {
    localStorage.setItem(SalesManagerDashboardFilterOption(account.hotel.id), JSON.stringify(salesFilterOptions))
    onChangeFilter(salesFilterOptions)
  }, [salesFilterOptions])

  useEffect(() => {
    if (!selectedMonth) {
      return
    }
    onChangeMonth(selectedMonth)
  }, [selectedMonth])

  useEffect(() => {
    getReservationsBoundaryDates().then(res => setReservationsBoundaryDates(res))
  }, [])

  const isFiltering = useMemo(() => {
    return (
      !salesFilterOptions.stayType.stay ||
      !salesFilterOptions.stayType.dayUse ||
      !salesFilterOptions.salesType.room ||
      !salesFilterOptions.salesType.other ||
      !salesFilterOptions.salesType.temporary
    )
  }, [salesFilterOptions])

  return (
    <div css={toolBarStyle}>
      <div className="left-block">
        <div className="mode-toggle-block">
          <Button
            buttonType={mode === DashboardType.Daily ? ButtonType.Primary : ButtonType.Secondary}
            width={51}
            height={27}
            onClick={() => onChangeMode(DashboardType.Daily)}
          >
            {t('Daily')}
          </Button>
          <div className="vertical-divide"></div>
          <Button
            buttonType={mode === DashboardType.Monthly ? ButtonType.Primary : ButtonType.Secondary}
            width={51}
            height={27}
            onClick={() => onChangeMode(DashboardType.Monthly)}
          >
            {t('Monthly')}
          </Button>
        </div>
        <div className="date-block">
          <div className="prev" onClick={() => moveMonth(-1)}>
            <img src={require('@/static/images/arrow_right_gray_nospace.svg')} />
            <span>{t('Previous month')}</span>
          </div>
          <MonthCascaderField
            value={month}
            onChange={setSelectedMonth}
            min={reservationsBoundaryDates?.min}
            max={reservationsBoundaryDates?.max}
            width="112px"
          />
          <div className="next" onClick={() => moveMonth(1)}>
            <span>{t('Next month')}</span>
            <img src={require('@/static/images/arrow_right_gray_nospace.svg')} />
          </div>
        </div>
        {mode === DashboardType.Monthly && (
          <div className="remarks">{t('Aggregate for each of the six months before and after this month, including this month')}</div>
        )}
      </div>
      <div className="right-block">
        <ToggleButton
          value={salesFilterOptions.isTaxIncluded}
          onChange={() => {
            setSalesFilterOptions({
              ...salesFilterOptions,
              isTaxIncluded: !salesFilterOptions.isTaxIncluded,
            })
          }}
          label={{ on: t('tax inc'), off: t('w/o tax') }}
        />
        <Button
          icon={isFiltering ? require('@/static/images/icon_filter_active.svg') : require('@/static/images/icon_filter_disable.svg')}
          buttonType={isFiltering ? ButtonType.Simple : ButtonType.NonStyle}
          width={120}
          height={32}
          onClick={() => setIsShowFilterPopup(!isShowFilterPopup)}
        >
          {t('Filter')}
        </Button>
        <Popup
          isOpen={isShowFilterPopup}
          onClose={() => setIsShowFilterPopup(false)}
          customStyle={css({ marginTop: 40, marginLeft: (-280 + 90) / 2 })}
        >
          <div css={filterPopupStyle}>
            <div>
              <div className="title-block">
                <div>{t('SalesManagerFilter.ACCOMType')}</div>
                <div
                  onClick={() => {
                    if (salesFilterOptions.stayType.stay && salesFilterOptions.stayType.dayUse) {
                      setSalesFilterOptions({
                        ...salesFilterOptions,
                        stayType: { stay: false, dayUse: false },
                      })
                    } else {
                      setSalesFilterOptions({
                        ...salesFilterOptions,
                        stayType: { stay: true, dayUse: true },
                      })
                    }
                  }}
                >
                  {salesFilterOptions.stayType.stay && salesFilterOptions.stayType.dayUse ? t('Deselect All') : t('Select All')}
                </div>
              </div>
              <div className="item-block">
                <Checkbox
                  value={salesFilterOptions.stayType.stay}
                  label={t('SalesManagerFilter.Accommodation')}
                  onChange={v => {
                    salesFilterOptions.stayType.stay = v
                    setSalesFilterOptions({ ...salesFilterOptions })
                  }}
                />
                <Checkbox
                  value={salesFilterOptions.stayType.dayUse}
                  label={t('SalesManagerFilter.DayUse')}
                  onChange={v => {
                    salesFilterOptions.stayType.dayUse = v
                    setSalesFilterOptions({ ...salesFilterOptions })
                  }}
                />
              </div>
            </div>
            <div>
              <div className="title-block">
                <div>{t('Sale category')}</div>
                <div
                  onClick={() => {
                    if (salesFilterOptions.salesType.room && salesFilterOptions.salesType.other && salesFilterOptions.salesType.temporary) {
                      setSalesFilterOptions({
                        ...salesFilterOptions,
                        salesType: { room: false, other: false, temporary: false },
                      })
                    } else {
                      setSalesFilterOptions({
                        ...salesFilterOptions,
                        salesType: { room: true, other: true, temporary: true },
                      })
                    }
                  }}
                >
                  {salesFilterOptions.salesType.room && salesFilterOptions.salesType.other && salesFilterOptions.salesType.temporary
                    ? t('Deselect All')
                    : t('Select All')}
                </div>
              </div>
              <div className="item-block">
                <Checkbox
                  value={salesFilterOptions.salesType.room}
                  label={t('Room sales')}
                  onChange={v => {
                    salesFilterOptions.salesType.room = v
                    setSalesFilterOptions({ ...salesFilterOptions })
                  }}
                />
                <Checkbox
                  value={salesFilterOptions.salesType.other}
                  label={t('Other sales')}
                  onChange={v => {
                    salesFilterOptions.salesType.other = v
                    setSalesFilterOptions({ ...salesFilterOptions })
                  }}
                />
                <Checkbox
                  value={salesFilterOptions.salesType.temporary}
                  label={t('Suspense receipt')}
                  onChange={v => {
                    salesFilterOptions.salesType.temporary = v
                    setSalesFilterOptions({ ...salesFilterOptions })
                  }}
                />
              </div>
            </div>
          </div>
        </Popup>
        <Link
          to={{
            pathname: '/sales-manager/detail-list',
            search: new URLSearchParams({
              beginDate: month.clone().startOf('month').format('YYYY-MM-DD'),
              endDate: month.clone().endOf('month').format('YYYY-MM-DD'),
            }).toString(),
          }}
          css={[linkButtonBaseStyle, secondaryLinkButtonStyle, css({ width: 74, height: 32 })]}
        >
          {t('Detail')}
        </Link>
        {mode === DashboardType.Daily && openSalesManagerId && (
          <Link
            to={{
              pathname: '/sales-manager/detail-day',
              search: new URLSearchParams({ id: openSalesManagerId }).toString(),
            }}
            css={[linkButtonBaseStyle, primaryLinkButtonStyle, css({ width: 118, height: 32 })]}
          >
            {t('Daily closing')}
          </Link>
        )}
      </div>
    </div>
  )
}
const toolBarStyle = css({
  display: 'flex',
  justifyContent: 'space-between',

  '*': { fontSize: '12px !important', fontWeight: 'bold' },
  '.left-block': {
    display: 'flex',
    gap: 32,
    alignItems: 'center',
    '.mode-toggle-block': {
      display: 'flex',
      gap: 16,
      alignSelf: 'center',
      button: {
        borderRadius: 5,
      },
    },
    '.date-block': {
      display: 'flex',
      alignItems: 'center',
      color: '#A3A3A3',
      '.prev, .next': {
        display: 'flex',
        alignItems: 'center',
        span: { padding: '0 8px' },
        img: { height: 14 },
        cursor: 'pointer',
      },
      '.prev': {
        span: { paddingRight: 16 },
        img: { transform: 'rotate(180deg)' },
      },
      '.next': {
        span: { paddingLeft: 16 },
      },
    },
  },
  '.right-block': {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  '.vertical-divide': {
    border: '0.5px solid #cccccc',
  },
  '.remarks': { color: '#676767', marginLeft: -8 },
})

const filterPopupStyle = css({
  padding: 16,
  width: 280,
  '> div': {
    paddingTop: 16,
    '&:first-of-type': { paddingTop: 0 },
  },
  '.title-block': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '> div:first-of-type': { fontSize: '14px !important' },
    '> div:last-of-type': { fontSize: '10px !important', cursor: 'pointer' },
  },
  '.item-block': {
    paddingTop: 8,
    display: 'flex',
    flexWrap: 'wrap',
    rowGap: 8,
    '> div': { width: '50%' },
  },
})

const linkButtonBaseStyle = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: 24,
  fontSize: 12,
  fontWeight: 'bold',
  textDecoration: 'none',
  cursor: 'pointer',
  boxSizing: 'border-box',
})

const secondaryLinkButtonStyle = css({
  color: '#f2a40b',
  border: '1px solid #f2a40b',
  background: '#FFF',
  '&:hover': {
    color: '#FFF',
    borderColor: '#F2A40B',
    background: '#F2A40B',
  },
})

const primaryLinkButtonStyle = css({
  color: '#fff',
  border: 'none',
  background: '#F2A40B',
  '&:hover': {
    background: '#D68E0F',
  },
})
